import React, { useContext, useEffect, useMemo, useState } from "react"
import firebase from "firebase/app"
import "firebase/firestore"

import { FirebaseContext } from "../idealline/Firebase"
import {
  buildBusinessClaim,
  businessClaimId,
  validateBusinessClaim,
} from "../../utils/businessClaim"

const trackEvent = (name, trackId) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", name, {
      event_category: "Business claims",
      event_label: String(trackId),
    })
  }
}

const FieldError = ({ children }) => children
  ? <span className="mt-1 block text-sm text-red-700" role="alert">{children}</span>
  : null

const BusinessClaimForm = ({ track }) => {
  const auth = useContext(FirebaseContext)
  const [values, setValues] = useState({
    claimantName: "",
    role: "",
    businessEmail: "",
    businessWebsite: "",
    verificationNote: "",
  })
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState("")

  useEffect(() => {
    if (!auth.user || auth.user.isAnonymous) return
    setValues(current => ({
      ...current,
      claimantName: current.claimantName || auth.user.displayName || auth.userPublic?.displayName || "",
      businessEmail: current.businessEmail || auth.user.email || "",
    }))
    trackEvent("business_claim_form_view", track.id)
  }, [auth.user, auth.userPublic, track.id])

  const claimInput = useMemo(() => ({
    ...values,
    trackId: track.id,
    trackName: track.name || "",
    trackSlug: track.slug || "",
    uid: auth.user && !auth.user.isAnonymous ? auth.user.uid : "",
    accountEmail: auth.user && auth.user.email || "",
  }), [auth.user, track, values])

  if (auth.user === null) {
    return <div className="py-12 text-center" aria-live="polite">Checking your account…</div>
  }

  if (!auth.user || auth.user.isAnonymous) {
    const redirect = typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}`
      : `/claim-business?track=${encodeURIComponent(track.id)}`
    return (
      <section className="rounded border border-gray-200 bg-gray-50 p-8 text-center">
        <h2 className="mb-3 text-2xl">Sign in to claim this business</h2>
        <p className="mb-6 text-gray-600">A verified Open Racer account keeps claim requests attributable and prevents anonymous submissions.</p>
        <a
          className="inline-flex rounded bg-indigo-700 px-6 py-3 font-bold text-white no-underline hover:bg-indigo-800"
          href={`/profile?redirect=${encodeURIComponent(redirect)}`}
          onClick={() => trackEvent("business_claim_login_required", track.id)}
        >
          Sign in or create an account
        </a>
      </section>
    )
  }

  if (submitted) {
    return (
      <section className="rounded border border-green-200 bg-green-50 p-8" aria-live="polite">
        <p className="mb-2 text-sm font-bold uppercase tracking-widest text-green-800">Pending review</p>
        <h2 className="mb-3 text-2xl">Your claim has been submitted</h2>
        <p className="mb-0 text-gray-700">We recorded your request for {track.name}. No listing-management access is granted until the claim is reviewed.</p>
      </section>
    )
  }

  const update = event => {
    const { name, value } = event.target
    setValues(current => ({ ...current, [name]: value }))
    setErrors(current => ({ ...current, [name]: undefined, form: undefined }))
    setSubmitError("")
  }

  const submit = async event => {
    event.preventDefault()
    const fieldErrors = validateBusinessClaim(claimInput)
    setErrors(fieldErrors)
    if (Object.keys(fieldErrors).length) return

    setSubmitting(true)
    setSubmitError("")
    try {
      const timestamp = firebase.firestore.FieldValue.serverTimestamp()
      const claim = buildBusinessClaim(claimInput, timestamp)
      const id = businessClaimId(track.id, auth.user.uid)
      await auth.app.firestore().collection("businessClaims").doc(id).set(claim)
      trackEvent("business_claim_submitted", track.id)
      setSubmitted(true)
    } catch (error) {
      console.error("Business claim submission failed", error)
      setSubmitError(error && error.code === "permission-denied"
        ? "A claim for this business already exists for your account, or the request could not be authorized."
        : "We could not submit your claim. Please try again.")
    } finally {
      setSubmitting(false)
    }
  }

  const inputClass = "mt-2 w-full rounded border border-gray-300 bg-white px-4 py-3 text-gray-900 focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-200"

  return (
    <form className="space-y-6" noValidate onSubmit={submit}>
      <div className="grid gap-6 md:grid-cols-2">
        <label className="block font-semibold text-gray-800">
          Full name
          <input className={inputClass} maxLength="120" name="claimantName" onChange={update} required value={values.claimantName} />
          <FieldError>{errors.claimantName}</FieldError>
        </label>
        <label className="block font-semibold text-gray-800">
          Role at the business
          <input className={inputClass} maxLength="120" name="role" onChange={update} placeholder="Owner, manager, marketing…" required value={values.role} />
          <FieldError>{errors.role}</FieldError>
        </label>
        <label className="block font-semibold text-gray-800">
          Business email
          <input className={inputClass} maxLength="254" name="businessEmail" onChange={update} required type="email" value={values.businessEmail} />
          <FieldError>{errors.businessEmail}</FieldError>
        </label>
        <label className="block font-semibold text-gray-800">
          Business website <span className="font-normal text-gray-500">(optional)</span>
          <input className={inputClass} maxLength="500" name="businessWebsite" onChange={update} placeholder="https://" type="url" value={values.businessWebsite} />
          <FieldError>{errors.businessWebsite}</FieldError>
        </label>
      </div>
      <label className="block font-semibold text-gray-800">
        How can we verify your relationship? <span className="font-normal text-gray-500">(optional)</span>
        <textarea className={inputClass} maxLength="2000" name="verificationNote" onChange={update} rows="5" value={values.verificationNote} />
        <FieldError>{errors.verificationNote}</FieldError>
      </label>
      {(errors.form || submitError) && <div className="rounded border border-red-200 bg-red-50 p-4 text-red-800" role="alert">{errors.form || submitError}</div>}
      <button className="rounded bg-indigo-700 px-7 py-3 font-bold text-white hover:bg-indigo-800 disabled:cursor-wait disabled:opacity-60" disabled={submitting} type="submit">
        {submitting ? "Submitting…" : "Submit claim"}
      </button>
    </form>
  )
}

export default BusinessClaimForm
